import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Settings, Phone, MapPin, Mail, Clock, Share2, ShieldCheck, Plus, Trash2, Headphones, FileCode, FileText, Database, Search, AlertTriangle, Info, Zap } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import catalogService from '../../services/catalogService';
import { toast } from 'react-toastify';
import ReactQuill, {Quill} from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { cleanHtmlContent, processHtmlAndUploadImages } from '../../utils/helpers';

const icons = Quill.import('ui/icons');
icons['unlink'] = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" x2="16" y1="12" y2="12"/><line x1="2" x2="22" y1="2" x2="22"/></svg>`;

const ICON_OPTIONS = [
    { value: 'Truck', label: 'Xe tải (Giao hàng)' },
    { value: 'RefreshCcw', label: 'Vòng lặp (Đổi trả)' },
    { value: 'CreditCard', label: 'Thẻ tín dụng (Thanh toán)' },
    { value: 'HeadphonesIcon', label: 'Tai nghe (Hỗ trợ)' },
    { value: 'ShieldCheck', label: 'Khiên bảo vệ (Cam kết)' },
    { value: 'Zap', label: 'Tia sét (Tốc độ)' },
    { value: 'Gift', label: 'Hộp quà (Khuyến mãi)' },
    { value: 'ThumbsUp', label: 'Ngón cái (Chất lượng)' },
    { value: 'Award', label: 'Huy chương (Uy tín)' },
    { value: 'Star', label: 'Ngôi sao (Nổi bật)' }
];

const AdminSettings = () => {
    const [loading, setLoading] = useState(false);
    const [isCleaning, setIsCleaning] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const [isFixingDB, setIsFixingDB] = useState(false);
    const [scanStats, setScanStats] = useState(null); 

    const [originalData, setOriginalData] = useState({
        configs: {},
        supportLines: '[]',
        productPolicies: '[]'
    });

    const [supportLines, setSupportLines] = useState([]);
    const [productPolicies, setProductPolicies] = useState([]);
    
    const [configs, setConfigs] = useState({
        hotline: '', email: '', address: '', rental_hotline: '',
        working_hours_morning: '', working_hours_afternoon: '',
        working_hours_evening: '', 
        zalo_link: '', messenger_link: '', facebook_link: '', youtube_link: '',
        footer_col3_title: '', footer_col4_title: '',
        slogan1_title: '', slogan1_desc: '', slogan1_icon: 'Truck',
        slogan2_title: '', slogan2_desc: '', slogan2_icon: 'RefreshCcw',
        slogan3_title: '', slogan3_desc: '', slogan3_icon: 'CreditCard',
        slogan4_title: '', slogan4_desc: '', slogan4_icon: 'HeadphonesIcon',
        shipping_info: '' 
    });

    const getQuillModules = useMemo(() => ({
        toolbar: {
            container: [
                ['bold', 'italic', 'underline'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                ['link', 'unlink'],
                ['clean']
            ],
            handlers: {
                unlink: function() {
                    this.quill.format('link', false);
                }
            }
        },
        clipboard: { matchVisual: false }
    }), []);

    const fetchConfigs = useCallback(async () => {
        try {
            const res = await catalogService.getSystemConfig();
            if (res && Object.keys(res).length > 0) {
                setConfigs(prev => ({ ...prev, ...res }));
                
                let fetchedSupport = [];
                let fetchedPolicies = [];

                if (res.support_lines) {
                    try { fetchedSupport = JSON.parse(res.support_lines); setSupportLines(fetchedSupport); } catch (e) {}
                }
                if (res.product_policies) {
                    try { fetchedPolicies = JSON.parse(res.product_policies); setProductPolicies(fetchedPolicies); } catch (e) {}
                }

                setOriginalData({
                    configs: { ...res },
                    supportLines: JSON.stringify(fetchedSupport),
                    productPolicies: JSON.stringify(fetchedPolicies)
                });
            }
        } catch (error) { console.error("Lỗi lấy config:", error); }
    }, []); 

    useEffect(() => { 
        fetchConfigs(); 
    }, [fetchConfigs]);

    const stripHtmlForComparison = (html) => {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return (doc.body.textContent || doc.body.innerText || "").replace(/\s+/g, ' ').trim();
    };

    const isConfigChanged = (key) => {
        const current = configs[key] || '';
        const original = originalData.configs[key] || '';

        if (key === 'shipping_info' || key.includes('content')) {
            return stripHtmlForComparison(current) !== stripHtmlForComparison(original);
        }

        return String(current).trim() !== String(original).trim();
    };

    const isSupportChanged = () => {
        return JSON.stringify(supportLines) !== originalData.supportLines;
    };

    const isPolicyChanged = () => {
        return JSON.stringify(productPolicies.map(p => ({...p, content: stripHtmlForComparison(p.content)}))) !== 
               JSON.stringify(JSON.parse(originalData.productPolicies).map(p => ({...p, content: stripHtmlForComparison(p.content)})));
    };

    const handleChange = (e) => setConfigs(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleShippingInfoChange = (content) => setConfigs(prev => ({ ...prev, shipping_info: content }));

    const handleSupportChange = (index, field, value) => {
        const newLines = [...supportLines];
        newLines[index][field] = value;
        setSupportLines(newLines);
    };
    const addSupportLine = () => setSupportLines([...supportLines, { title: '', phone: '' }]);
    const removeSupportLine = (index) => setSupportLines(supportLines.filter((_, i) => i !== index));

    const handlePolicyChange = (index, field, value) => {
        const newPolicies = [...productPolicies];
        newPolicies[index][field] = value;
        setProductPolicies(newPolicies);
    };
    const addPolicy = () => {
        setProductPolicies([...productPolicies, { id: Date.now(), title: 'Mục mới', icon: 'ShieldCheck', content: '' }]);
    };
    const removePolicy = (index) => {
        if(window.confirm("Bạn có chắc muốn xóa mục này?")) {
            setProductPolicies(productPolicies.filter((_, i) => i !== index));
        }
    };

    const handleSave = async () => {
        setLoading(true);
        const toastId = toast.loading("Đang xử lý dữ liệu và lưu cấu hình...");
        try {
            const cleanedPolicies = await Promise.all(
                productPolicies.map(async (policy) => {
                    const cleanContent = await processHtmlAndUploadImages(
                        policy.content,
                        'image_config',
                        adminStaffService.uploadEditorImage,
                        adminStaffService.uploadExternalEditorImage
                    );
                    return { ...policy, content: cleanHtmlContent(cleanContent) };
                })
            );

            const cleanShippingInfo = await processHtmlAndUploadImages(
                configs.shipping_info || '',
                'image_config',
                adminStaffService.uploadEditorImage,
                adminStaffService.uploadExternalEditorImage
            );

            const payload = {
                ...configs,
                shipping_info: cleanHtmlContent(cleanShippingInfo),
                support_lines: JSON.stringify(supportLines),
                product_policies: JSON.stringify(cleanedPolicies)
            };

            await adminStaffService.updateSystemConfig(payload);
            toast.update(toastId, { render: "Lưu cấu hình hệ thống thành công!", type: "success", isLoading: false, autoClose: 2000 });
            await fetchConfigs();
        } catch (error) { 
            toast.update(toastId, { render: "Lỗi khi lưu cấu hình!", type: "error", isLoading: false, autoClose: 3000 });
        } finally { setLoading(false); }
    };

    const handleScanImages = async () => {
        setIsCleaning(true);
        const toastId = toast.loading("Đang rà soát máy chủ...");
        try {
            const res = await adminStaffService.scanImages();
            setScanStats(res.data || res);
            toast.update(toastId, { render: "Quét hoàn tất!", type: "success", isLoading: false, autoClose: 2000 });
        } catch (error) {
            toast.update(toastId, { render: "Lỗi quét!", type: "error", isLoading: false, autoClose: 3000 });
        } finally { setIsCleaning(false); }
    };

    const handleConfirmCleanup = async () => {
        if (!window.confirm("Xóa ảnh rác?")) return;
        setIsCleaning(true);
        try {
            await adminStaffService.cleanupImages();
            toast.success("Dọn dẹp thành công!");
            setScanStats(prev => ({ ...prev, orphanedImages: 0 })); 
        } catch (error) { toast.error("Lỗi!"); } finally { setIsCleaning(false); }
    };

    const handleConvertWebp = async () => {
        if (!window.confirm("Convert sang WebP?")) return;
        setIsConverting(true);
        try {
            await adminStaffService.convertWebpImages();
            toast.success("Convert thành công!");
            setScanStats(prev => ({ ...prev, nonWebpImages: 0 })); 
        } catch (error) { toast.error("Lỗi!"); } finally { setIsConverting(false); }
    };

    const handleFixDatabaseLinks = async () => {
        if (!window.confirm("Hành động này sẽ tìm và thay thế toàn bộ IP cũ (171.244.8.71) thành tên miền mới trong Database. Bạn có chắc chắn? (Khuyên dùng: Hãy backup Database trước khi chạy)")) return;
        setIsFixingDB(true);
        const toastId = toast.loading("Đang cập nhật đường dẫn ảnh trong Database...");
        try {
            await adminStaffService.fixDatabaseImageLinks();
            toast.update(toastId, { render: "Đồng bộ link ảnh Database thành công!", type: "success", isLoading: false, autoClose: 3000 });
        } catch (error) { 
            toast.update(toastId, { render: "Có lỗi xảy ra khi cập nhật Database!", type: "error", isLoading: false, autoClose: 3000 });
        } finally { 
            setIsFixingDB(false); 
        }
    };

    const ChangeBadge = () => <span className="ml-2 text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded font-bold uppercase animate-pulse">📝 Có thay đổi</span>;

    return (
        <div className="max-w-6xl mx-auto pb-10">
            <style dangerouslySetInnerHTML={{__html: `
                .ql-snow .ql-toolbar button.ql-unlink svg { width: 18px; height: 18px; }
                .ql-snow .ql-toolbar button.ql-unlink:hover { color: #06c; }
                
                /* 👉 THIẾT LẬP CHIỀU CAO TỰ ĐỘNG THEO NỘI DUNG CHO SHIPPING INFO */
                .shipping-quill .ql-editor { 
                    min-height: 50px; 
                    height: auto !important; 
                    overflow-y: hidden;
                }
                .shipping-quill .ql-container.ql-snow { border: none; }
            `}} />
            
            <div className="flex items-center justify-between mb-6 bg-gray-50/90 backdrop-blur py-4 px-2 border-b sticky top-0 z-[100] shadow-sm">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Settings size={26} className="text-tnc-orange" /> Cài đặt Hệ thống
                </h2>
                <button 
                    onClick={handleSave} disabled={loading || isCleaning || isConverting} 
                    className="flex items-center gap-2 bg-tnc-orange hover:bg-tnc-orange-hover text-white px-6 py-2.5 rounded-md font-bold shadow-md transition-all active:scale-95 disabled:bg-gray-400"
                >
                    {loading ? "Đang lưu..." : <><Save size={20} /> LƯU THAY ĐỔI</>}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-[#002855] mb-6 border-b pb-3 flex items-center gap-2">
                        <MapPin size={20} /> Thông tin Cửa hàng
                    </h3>
                    <div className="space-y-5">
                        <div>
                            <label className="flex items-center text-sm font-bold text-gray-700 mb-2 gap-1">
                                <Phone size={16}/> Hotline {isConfigChanged('hotline') && <ChangeBadge/>}
                            </label>
                            <input name="hotline" value={configs.hotline || ''} onChange={handleChange} className={`w-full px-4 py-2.5 rounded-lg border outline-none font-bold text-tnc-orange ${isConfigChanged('hotline') ? 'border-yellow-400 bg-yellow-50/30' : 'border-gray-300'}`} />
                        </div>
                        <div>
                            <label className="flex items-center text-sm font-bold text-gray-700 mb-2 gap-1">
                                <Phone size={16}/> Hotline Thuê máy {isConfigChanged('rental_hotline') && <ChangeBadge/>}
                            </label>
                            <input name="rental_hotline" value={configs.rental_hotline || ''} onChange={handleChange} className={`w-full px-4 py-2.5 rounded-lg border outline-none font-bold text-tnc-orange ${isConfigChanged('rental_hotline') ? 'border-yellow-400 bg-yellow-50/30' : 'border-gray-300'}`} />
                        </div>
                        <div>
                            <label className="flex items-center text-sm font-bold text-gray-700 mb-2 gap-1">
                                <Mail size={16}/> Email {isConfigChanged('email') && <ChangeBadge/>}
                            </label>
                            <input name="email" value={configs.email || ''} onChange={handleChange} className={`w-full px-4 py-2.5 rounded-lg border outline-none ${isConfigChanged('email') ? 'border-yellow-400 bg-yellow-50/30' : 'border-gray-300'}`} />
                        </div>
                        <div>
                            <label className="flex items-center text-sm font-bold text-gray-700 mb-2 gap-1">
                                <MapPin size={16}/> Địa chỉ {isConfigChanged('address') && <ChangeBadge/>}
                            </label>
                            <textarea name="address" rows="3" value={configs.address || ''} onChange={handleChange} className={`w-full px-4 py-2.5 rounded-lg border outline-none resize-none ${isConfigChanged('address') ? 'border-yellow-400 bg-yellow-50/30' : 'border-gray-300'}`} />
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className={`bg-white p-6 md:p-8 rounded-xl shadow-sm border ${isSupportChanged() ? 'border-yellow-400' : 'border-gray-100'}`}>
                        <h3 className="text-lg font-bold text-[#002855] mb-6 border-b pb-3 flex items-center justify-between">
                            <span className="flex items-center gap-2"><Headphones size={20} /> Hỗ trợ {isSupportChanged() && <ChangeBadge/>}</span>
                            <button onClick={addSupportLine} className="text-sm bg-blue-50 text-blue-600 px-3 py-1.5 rounded flex items-center gap-1 font-bold hover:bg-blue-100">
                                <Plus size={14}/> Thêm dòng
                            </button>
                        </h3>
                        <div className="space-y-3">
                            {supportLines.map((line, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <input placeholder="Tiêu đề" value={line.title} onChange={(e) => handleSupportChange(idx, 'title', e.target.value)} className="w-1/2 px-3 py-2 rounded border border-gray-300 text-sm" />
                                    <input placeholder="Số ĐT" value={line.phone} onChange={(e) => handleSupportChange(idx, 'phone', e.target.value)} className="w-1/2 px-3 py-2 rounded border border-gray-300 text-sm font-bold text-tnc-orange" />
                                    <button onClick={() => removeSupportLine(idx)} className="p-2 text-gray-400 hover:text-red-500 bg-gray-50 rounded"><Trash2 size={16}/></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-lg font-bold text-[#002855] mb-6 border-b pb-3 flex items-center gap-2">
                            <Clock size={20} /> Lịch làm việc
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {['morning', 'afternoon', 'evening'].map(ca => {
                                const key = `working_hours_${ca}`;
                                return (
                                    <div key={ca}>
                                        <label className="text-xs font-bold text-gray-500 mb-2 block uppercase tracking-wider">Ca {ca === 'morning' ? 'Sáng' : ca === 'afternoon' ? 'Chiều' : 'Tối'} {isConfigChanged(key) && <ChangeBadge/>}</label>
                                        <input name={key} value={configs[key] || ''} onChange={handleChange} className={`w-full px-3 py-2 rounded-lg border text-sm ${isConfigChanged(key) ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* BLOCK 2: THÔNG TIN GIAO HÀNG */}
            <div className={`bg-white p-6 md:p-8 rounded-xl shadow-sm border mb-6 ${isConfigChanged('shipping_info') ? 'border-yellow-400' : 'border-gray-100'}`}>
                <h3 className="text-lg font-bold text-[#002855] mb-4 flex items-center gap-2">
                    <FileText size={20} /> Thông tin Giao hàng (Trang Checkout) {isConfigChanged('shipping_info') && <ChangeBadge/>}
                </h3>
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <ReactQuill 
                        theme="snow" 
                        value={configs.shipping_info || ''} 
                        onChange={handleShippingInfoChange} 
                        modules={{ toolbar: [[{ 'header': [2, 3, false] }], ['bold', 'italic', 'underline'], [{ 'list': 'ordered'}, { 'list': 'bullet' }], ['link', 'image', 'clean']] }} 
                        className="shipping-quill"
                    />
                </div>
            </div>

            {/* BLOCK 3: PRODUCT POLICIES */}
            <div className={`bg-white p-6 md:p-8 rounded-xl shadow-sm border mb-6 ${isPolicyChanged() ? 'border-yellow-400' : 'border-gray-100'}`}>
                <div className="flex justify-between items-center border-b pb-3 mb-6">
                    <h3 className="text-lg font-bold text-[#002855] flex items-center gap-2">
                        <FileCode size={20} /> Khối Cam kết (Chi tiết SP) {isPolicyChanged() && <ChangeBadge/>}
                    </h3>
                    <button onClick={addPolicy} className="bg-tnc-orange text-white px-4 py-2 rounded-lg font-bold hover:bg-tnc-orange-hover shadow-md flex items-center gap-1">
                        <Plus size={18}/> Thêm mục
                    </button>
                </div>
                <div className="space-y-6">
                    {productPolicies.map((policy, idx) => (
                        <div key={policy.id || idx} className="p-5 border border-gray-200 rounded-xl bg-gray-50/50 relative group">
                            <button onClick={() => removePolicy(idx)} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pr-12">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 mb-1 block uppercase tracking-wider">Tiêu đề</label>
                                    <input value={policy.title} onChange={(e) => handlePolicyChange(idx, 'title', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white outline-none" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 mb-1 block uppercase tracking-wider">Biểu tượng</label>
                                    <select value={policy.icon} onChange={(e) => handlePolicyChange(idx, 'icon', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white outline-none">
                                        {ICON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <ReactQuill theme="snow" value={policy.content} onChange={(val) => handlePolicyChange(idx, 'content', val)} modules={getQuillModules} className="bg-white rounded-lg border border-gray-200" />
                        </div>
                    ))}
                </div>
            </div>

            {/* MẠNG XÃ HỘI */}
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 mb-6">
                <h3 className="text-lg font-bold text-[#002855] mb-6 border-b pb-3 flex items-center gap-2"><Share2 size={20} /> Mạng Xã Hội</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {['facebook_link', 'youtube_link', 'zalo_link', 'messenger_link'].map(field => (
                        <div key={field}>
                            <label className="text-sm font-bold text-gray-700 mb-2 block capitalize">{field.replace('_', ' ')} {isConfigChanged(field) && <ChangeBadge/>}</label>
                            <input name={field} value={configs[field] || ''} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border outline-none ${isConfigChanged(field) ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`} />
                        </div>
                    ))}
                </div>
            </div>

            {/* SLOGANS */}
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 mb-6">
                <h3 className="text-lg font-bold text-[#002855] mb-6 border-b pb-3 flex items-center gap-2"><ShieldCheck size={20} /> Slogan Cam kết Trang chủ</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(num => (
                        <div key={num} className={`p-4 rounded-xl border ${isConfigChanged(`slogan${num}_title`) ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'}`}>
                            <h4 className="font-bold text-xs text-tnc-orange mb-3 uppercase tracking-tighter">Slogan {num} {isConfigChanged(`slogan${num}_title`) && <ChangeBadge/>}</h4>
                            <select name={`slogan${num}_icon`} value={configs[`slogan${num}_icon`] || ''} onChange={handleChange} className="w-full text-xs p-2 mb-2 border rounded-lg bg-white outline-none">
                                {ICON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                            <input name={`slogan${num}_title`} value={configs[`slogan${num}_title`] || ''} placeholder="Tiêu đề chính" onChange={handleChange} className="w-full text-xs p-2 mb-2 border rounded-lg outline-none" />
                            <input name={`slogan${num}_desc`} value={configs[`slogan${num}_desc`] || ''} placeholder="Mô tả" onChange={handleChange} className="w-full text-xs p-2 border rounded-lg outline-none" />
                        </div>
                    ))}
                </div>
            </div>

            {/* BẢO TRÌ */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-blue-200 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-5"><Database size={100}/></div>
                <h3 className="text-lg font-bold text-blue-800 mb-2 flex items-center gap-2"><Database size={20} /> Bảo trì Hình ảnh</h3>
                {!scanStats ? (
                    <button onClick={handleScanImages} disabled={loading || isCleaning} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-md transition-all disabled:bg-gray-300"><Search size={18}/> Bắt đầu quét hệ thống</button>
                ) : (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-white border p-4 rounded-xl text-center shadow-sm"><span className="block text-[10px] text-gray-400 font-bold uppercase">Tổng file</span><b className="text-xl text-gray-700">{scanStats.totalPhysical}</b></div>
                            <div className="bg-green-50 border border-green-100 p-4 rounded-xl shadow-sm text-center text-green-700"><span className="block text-[10px] font-bold uppercase flex justify-center items-center gap-1"><ShieldCheck size={12}/> An toàn</span><b className="text-xl">{scanStats.usedImages}</b></div>
                            <div className="bg-red-50 border border-red-200 p-4 rounded-xl shadow-sm text-center text-red-600 animate-pulse"><span className="block text-[10px] font-bold uppercase flex justify-center items-center gap-1"><AlertTriangle size={12}/> Ảnh rác</span><b className="text-xl">{scanStats.orphanedImages}</b></div>
                            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl shadow-sm text-center text-yellow-700"><span className="block text-[10px] font-bold uppercase flex justify-center items-center gap-1"><Zap size={12}/> Chưa nén</span><b className="text-xl">{scanStats.nonWebpImages}</b></div>
                        </div>
                        {scanStats.details && (
                            <div className="pt-4 border-t border-blue-100">
                                <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                                    <Info size={16} className="text-blue-500"/> Chi tiết Phân bổ Ảnh (Đang dùng)
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {Object.entries(scanStats.details).map(([categoryName, count]) => (
                                        <div key={categoryName} className="flex justify-between items-center bg-blue-50/50 px-4 py-2.5 rounded-lg border border-blue-100">
                                            <span className="text-[13px] font-medium text-gray-600">{categoryName}</span>
                                            <span className="text-sm font-black text-blue-800 bg-white px-2 py-0.5 rounded shadow-sm">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-3">
                            {scanStats.orphanedImages > 0 && <button onClick={handleConfirmCleanup} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-sm transition-all"><Trash2 size={18}/> Xóa ngay</button>}
                            {scanStats.nonWebpImages > 0 && <button onClick={handleConvertWebp} className="bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-sm transition-all"><Zap size={18}/> Tối ưu WebP</button>}
                            <button onClick={handleFixDatabaseLinks} disabled={isFixingDB} className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-sm transition-all disabled:bg-gray-400">
                                {isFixingDB ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : <Database size={18}/>}
                                Đồng bộ Link DB
                            </button>
                            <button onClick={() => setScanStats(null)} className="text-gray-400 font-bold px-4 hover:text-gray-600">Đóng</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSettings;